
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Check, X, Users, Clock, Bell, UserMinus, BarChart3, CheckCircle, AlertCircle, Settings } from 'lucide-react';
import { useNotifications } from '@/hooks/useNotifications';

interface NotificationsListProps {
  onClose?: () => void;
}

const NotificationsList = ({ onClose }: NotificationsListProps) => {
  const { notifications, loading, markAsRead, handleInvitationResponse } = useNotifications();

  const handleAcceptInvitation = async (notification: any) => {
    const invitationId = notification.data?.invitation_id;
    if (invitationId) {
      const success = await handleInvitationResponse(invitationId, 'accepted', notification.id);
      if (success && onClose) {
        onClose();
      }
    } else {
      console.log('[notifications] missing invitation_id on notification', notification);
    }
  };

  const handleDeclineInvitation = async (notification: any) => {
    const invitationId = notification.data?.invitation_id;
    if (invitationId) {
      const success = await handleInvitationResponse(invitationId, 'declined', notification.id);
      if (success && onClose) {
        onClose();
      }
    } else {
      console.log('[notifications] missing invitation_id on notification', notification);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await markAsRead(notificationId);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'project_invitation':
        return <Users className="w-5 h-5 text-blue-600" />;
      case 'project_removal':
        return <UserMinus className="w-5 h-5 text-red-600" />;
      case 'weekly_report':
        return <BarChart3 className="w-5 h-5 text-green-600" />;
      case 'address_validation':
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'system_update':
        return <Settings className="w-5 h-5 text-purple-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const getNotificationBadge = (type: string, data: any) => {
    switch (type) {
      case 'weekly_report':
        return (
          <Badge variant="outline" className="text-green-600 border-green-200">
            Weekly Report
          </Badge>
        );
      case 'address_validation':
        const isSuccess = data?.validation_result === 'success' || data?.validation_result === 'validated';
        return (
          <Badge variant="outline" className={isSuccess ? "text-green-600 border-green-200" : "text-red-600 border-red-200"}>
            {isSuccess ? 'Validation Complete' : 'Validation Failed'}
          </Badge>
        );
      case 'system_update':
        return (
          <Badge variant="outline" className="text-purple-600 border-purple-200">
            System Update
          </Badge>
        );
      case 'system':
        return (
          <Badge variant="outline" className="text-blue-600 border-blue-200">
            System
          </Badge>
        );
      case 'project_removal':
        return (
          <Badge variant="outline" className="text-red-600 border-red-200">
            Project Access Removed
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-h-96 overflow-y-auto">
      <div className="p-3 border-b">
        <h3 className="font-semibold text-sm">Notifications</h3>
      </div>
      
      {notifications.length === 0 ? (
        <div className="p-4 text-center text-gray-500 text-sm">
          No notifications yet
        </div>
      ) : (
        <div className="divide-y">
          {notifications.map((notification) => (
            <div key={notification.id} className="p-3 hover:bg-gray-50">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  {getNotificationIcon(notification.notification_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900">
                      {notification.title}
                    </p>
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600 mt-1">
                    {notification.message}
                  </p>
                  
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center text-xs text-gray-400">
                      <Clock className="w-3 h-3 mr-1" />
                      {new Date(notification.created_at).toLocaleDateString()}
                    </div>
                    {getNotificationBadge(notification.notification_type, notification.data)}
                  </div>
                  
                  {notification.notification_type === 'project_invitation' && !notification.is_read && (
                    <div className="flex space-x-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleAcceptInvitation(notification)}
                        className="h-7 px-3 text-xs"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeclineInvitation(notification)}
                        className="h-7 px-3 text-xs"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Decline
                      </Button>
                    </div>
                  )}
                  
                  {!notification.is_read && notification.notification_type !== 'project_invitation' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleMarkAsRead(notification.id)}
                      className="h-6 px-2 text-xs mt-2"
                    >
                      Mark as read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsList;
